import { useEffect } from 'react';
import Cookies from 'js-cookie';

// Configuration des IDs - Mis à jour avec vos vrais IDs
export const TRACKING_CONFIG = {
  GTM_ID: 'GTM-KCTRJQ3G', // Votre Google Tag Manager ID
  GA4_ID: 'G-8PCEJTHR86', // Votre Google Analytics 4 ID
  FB_PIXEL_ID: 'YOUR_FB_PIXEL_ID', // À remplacer par votre Facebook Pixel ID
  LINKEDIN_PARTNER_ID: '7611090', // LinkedIn Partner ID NoCodeO
  HOTJAR_ID: 'YOUR_HOTJAR_ID', // À remplacer par votre Hotjar Site ID
  COOKIE_CONSENT_NAME: 'nocodeo_cookie_consent',
  COOKIE_PREFERENCES_NAME: 'nocodeo_cookie_preferences'
};

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
    fbq: (...args: any[]) => void;
    _fbq: any;
    lintrk: (...args: any[]) => void;
    hj: (...args: any[]) => void;
    _hjSettings: any;
  }
}

// Types pour le consentement cookies
export type CookieConsentStatus = 'accepted' | 'rejected' | 'pending';

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

export const DEFAULT_COOKIE_PREFERENCES: CookiePreferences = {
  necessary: true, // Toujours true
  analytics: false,
  marketing: false,
  functional: false
};

// Initialiser Google Tag Manager
const GTM_INLINE_SCRIPT_ID = 'nocodeo-gtm-inline';
const GA4_SRC_SCRIPT_ID = 'nocodeo-ga4-src';
const GA4_CONFIG_SCRIPT_ID = 'nocodeo-ga4-config';
const FB_PIXEL_SCRIPT_ID = 'nocodeo-fb-pixel';
const FB_PIXEL_NOSCRIPT_ID = 'nocodeo-fb-pixel-noscript';
const LINKEDIN_SCRIPT_ID = 'nocodeo-linkedin';
const LINKEDIN_NOSCRIPT_ID = 'nocodeo-linkedin-noscript';
const HOTJAR_SCRIPT_ID = 'nocodeo-hotjar';

const ensureGtag = () => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function() {
    window.dataLayer.push(arguments);
  };
};

const appendInlineScript = (id: string, content: string) => {
  if (typeof document === 'undefined') return;
  let script = document.getElementById(id) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.innerHTML = content;
    document.head.appendChild(script);
  }
};

const appendExternalScript = (id: string, src: string, options?: { async?: boolean }) => {
  if (typeof document === 'undefined') return;
  let script = document.getElementById(id) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.src = src;
    if (options?.async) {
      script.async = true;
    }
    document.head.appendChild(script);
  }
};

const appendNoScript = (id: string, content: string) => {
  if (typeof document === 'undefined') return;
  let noscript = document.getElementById(id) as HTMLElement | null;
  if (!noscript) {
    noscript = document.createElement('noscript');
    noscript.id = id;
    noscript.innerHTML = content;
    (document.body || document.head).appendChild(noscript);
  }
};

export const initGTM = () => {
  if (typeof window === 'undefined') return;

  ensureGtag();

  appendInlineScript(
    GTM_INLINE_SCRIPT_ID,
    `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${TRACKING_CONFIG.GTM_ID}');
  `
  );
};

// Initialiser Facebook Pixel
export const initFacebookPixel = () => {
  if (typeof window === 'undefined' || TRACKING_CONFIG.FB_PIXEL_ID === 'YOUR_FB_PIXEL_ID') return;

  if (window.fbq) {
    return;
  }

  // Script Facebook Pixel
  appendInlineScript(
    FB_PIXEL_SCRIPT_ID,
    `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${TRACKING_CONFIG.FB_PIXEL_ID}');
    fbq('track', 'PageView');
  `
  );

  // Pixel image pour navigateurs sans JS
  appendNoScript(
    FB_PIXEL_NOSCRIPT_ID,
    `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${TRACKING_CONFIG.FB_PIXEL_ID}&ev=PageView&noscript=1" />`
  );
};

// Initialiser LinkedIn Insight Tag
export const initLinkedInInsight = () => {
  if (typeof window === 'undefined' || TRACKING_CONFIG.LINKEDIN_PARTNER_ID === 'YOUR_LINKEDIN_ID') return;

  if (window.lintrk) {
    return;
  }

  appendInlineScript(
    LINKEDIN_SCRIPT_ID,
    `
    _linkedin_partner_id = "${TRACKING_CONFIG.LINKEDIN_PARTNER_ID}";
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(_linkedin_partner_id);
    (function(l) {
      if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
      window.lintrk.q=[]}
      var s = document.getElementsByTagName("script")[0];
      var b = document.createElement("script");
      b.type = "text/javascript";b.async = true;
      b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
      s.parentNode.insertBefore(b, s);
    })(window.lintrk);
  `
  );

  // Pixel image pour navigateurs sans JS
  appendNoScript(
    LINKEDIN_NOSCRIPT_ID,
    `<img height="1" width="1" style="display:none;" alt="" src="https://px.ads.linkedin.com/collect/?pid=${TRACKING_CONFIG.LINKEDIN_PARTNER_ID}&fmt=gif" />`
  );
};

// Initialiser Hotjar
export const initHotjar = () => {
  if (typeof window === 'undefined' || TRACKING_CONFIG.HOTJAR_ID === 'YOUR_HOTJAR_ID') return;

  if (window.hj) {
    return;
  }

  appendInlineScript(
    HOTJAR_SCRIPT_ID,
    `
    (function(h,o,t,j,a,r){
      h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
      h._hjSettings={hjid:${TRACKING_CONFIG.HOTJAR_ID},hjsv:6};
      a=o.getElementsByTagName('head')[0];
      r=o.createElement('script');r.async=1;
      r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
      a.appendChild(r);
    })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
  `
  );
};

// Initialiser Google Analytics 4
export const initGA4 = () => {
  if (typeof window === 'undefined') return;

  ensureGtag();

  // Script GA4
  appendExternalScript(
    GA4_SRC_SCRIPT_ID,
    `https://www.googletagmanager.com/gtag/js?id=${TRACKING_CONFIG.GA4_ID}`,
    { async: true }
  );

  // Configuration GA4
  appendInlineScript(
    GA4_CONFIG_SCRIPT_ID,
    `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${TRACKING_CONFIG.GA4_ID}', {
      send_page_view: true,
      cookie_flags: 'secure;samesite=lax'
    });
  `
  );
};

const buildConsentUpdate = (
  consentStatus: CookieConsentStatus,
  preferences: CookiePreferences
) => {
  const isAccepted = consentStatus === 'accepted';
  return {
    analytics_storage: isAccepted && preferences.analytics ? 'granted' : 'denied',
    ad_storage: isAccepted && preferences.marketing ? 'granted' : 'denied',
    functionality_storage: isAccepted && preferences.functional ? 'granted' : 'denied',
    personalization_storage: isAccepted && preferences.marketing ? 'granted' : 'denied'
  } as const;
};

const applyConsentPreferences = (
  consentStatus: CookieConsentStatus,
  preferences: CookiePreferences
) => {
  if (typeof window === 'undefined') return;

  ensureGtag();
  window.gtag('consent', 'update', buildConsentUpdate(consentStatus, preferences));

  if (consentStatus !== 'accepted') {
    return;
  }

  if (preferences.analytics) {
    initGA4();
    initGTM();
  }

  if (preferences.marketing) {
    initFacebookPixel();
    initLinkedInInsight();
  }

  if (preferences.functional) {
    initHotjar();
  }
};

// Vérifier le statut du consentement
export const getCookieConsentStatus = (): CookieConsentStatus => {
  const consent = Cookies.get(TRACKING_CONFIG.COOKIE_CONSENT_NAME);
  if (consent === 'accepted') return 'accepted';
  if (consent === 'rejected') return 'rejected';
  return 'pending';
};

// Obtenir les préférences cookies
export const getCookiePreferences = (): CookiePreferences => {
  const preferences = Cookies.get(TRACKING_CONFIG.COOKIE_PREFERENCES_NAME);
  if (preferences) {
    try {
      return JSON.parse(preferences);
    } catch {
      return DEFAULT_COOKIE_PREFERENCES;
    }
  }
  return DEFAULT_COOKIE_PREFERENCES;
};

// Sauvegarder les préférences cookies
export const saveCookiePreferences = (
  preferences: CookiePreferences,
  options?: { consentStatus?: CookieConsentStatus }
) => {
  const consentStatus = options?.consentStatus ?? 'accepted';

  Cookies.set(TRACKING_CONFIG.COOKIE_CONSENT_NAME, consentStatus, {
    expires: 365,
    secure: true,
    sameSite: 'lax'
  });

  Cookies.set(TRACKING_CONFIG.COOKIE_PREFERENCES_NAME, JSON.stringify(preferences), {
    expires: 365,
    secure: true,
    sameSite: 'lax'
  });

  applyConsentPreferences(consentStatus, preferences);
};

// Accepter tous les cookies
export const acceptAllCookies = () => {
  const preferences: CookiePreferences = {
    necessary: true,
    analytics: true,
    marketing: true,
    functional: true
  };
  saveCookiePreferences(preferences, { consentStatus: 'accepted' });
};

// Rejeter les cookies non-essentiels
export const rejectNonEssentialCookies = () => {
  saveCookiePreferences(DEFAULT_COOKIE_PREFERENCES, { consentStatus: 'rejected' });
};

// Hook pour initialiser le tracking
export const useTracking = () => {
  useEffect(() => {
    const consentStatus = getCookieConsentStatus();
    const preferences = getCookiePreferences();

    // Configurer le consentement par défaut
    if (typeof window !== 'undefined') {
      ensureGtag();

      // Consentement par défaut (refusé)
      window.gtag('consent', 'default', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        functionality_storage: 'denied',
        personalization_storage: 'denied'
      });
    }

    // Si consentement déjà donné, initialiser
    if (consentStatus === 'accepted') {
      applyConsentPreferences(consentStatus, preferences);
    } else if (consentStatus === 'rejected' && typeof window !== 'undefined') {
      window.gtag('consent', 'update', buildConsentUpdate(consentStatus, preferences));
    }
  }, []);
};

// Fonctions de tracking des événements
export const trackEvent = (eventName: string, parameters?: any) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, parameters);
  }
};

export const trackPageView = (pagePath: string, pageTitle: string) => {
  if (typeof window !== 'undefined') {
    ensureGtag();
    window.gtag('config', TRACKING_CONFIG.GA4_ID, {
      page_path: pagePath,
      page_title: pageTitle
    });
  }
};

export const trackPurchase = (transactionId: string, value: number, currency: string = 'EUR') => {
  trackEvent('purchase', {
    transaction_id: transactionId,
    value: value,
    currency: currency
  });
};

export const trackFormationEnrollment = (formationId: string, formationName: string, price: number) => {
  trackEvent('formation_enrollment', {
    formation_id: formationId,
    formation_name: formationName,
    value: price,
    currency: 'EUR'
  });
};

export const trackFormSubmission = (formName: string) => {
  trackEvent('form_submit', {
    form_name: formName
  });
};

// Fonctions de tracking Facebook Pixel
export const trackFacebookEvent = (eventName: string, parameters?: any) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, parameters);
  }
};

export const trackFacebookPurchase = (value: number, currency: string = 'EUR') => {
  trackFacebookEvent('Purchase', { value, currency });
};

export const trackFacebookLead = (formName: string) => {
  trackFacebookEvent('Lead', { content_name: formName });
};

// Fonctions de tracking LinkedIn
export const trackLinkedInEvent = (eventType: string, conversionId?: string) => {
  if (typeof window !== 'undefined' && window.lintrk) {
    if (conversionId) {
      window.lintrk('track', { conversion_id: conversionId });
    } else {
      window.lintrk('track', { event_type: eventType });
    }
  }
};

export const trackLinkedInConversion = (conversionId: string) => {
  trackLinkedInEvent('conversion', conversionId);
};

// Fonctions de tracking Hotjar
export const trackHotjarEvent = (eventName: string) => {
  if (typeof window !== 'undefined' && window.hj) {
    window.hj('event', eventName);
  }
};

export const trackHotjarFormSubmit = (formName: string) => {
  trackHotjarEvent(`form_submit_${formName}`);
};

// Tracking e-commerce avancé
export const trackPurchaseComplete = (transactionData: {
  transactionId: string;
  value: number;
  currency?: string;
  items?: Array<{
    name: string;
    category: string;
    quantity: number;
    price: number;
  }>;
}) => {
  const { transactionId, value, currency = 'EUR', items = [] } = transactionData;
  
  // Google Analytics
  trackEvent('purchase', {
    transaction_id: transactionId,
    value,
    currency,
    items
  });
  
  // Facebook Pixel
  trackFacebookPurchase(value, currency);
  
  // Hotjar
  trackHotjarEvent('purchase_complete');
};

export const trackFormationViewDetails = (formationId: string, formationName: string) => {
  // Google Analytics
  trackEvent('view_item', {
    item_id: formationId,
    item_name: formationName,
    item_category: 'formation'
  });
  
  // Facebook Pixel
  trackFacebookEvent('ViewContent', {
    content_ids: [formationId],
    content_type: 'product',
    content_name: formationName
  });
  
  // Hotjar
  trackHotjarEvent(`formation_view_${formationId}`);
};
